/* THE WALKTHROUGH, one script per seat.
 *
 * Each step points at a REAL element on a REAL screen, dims the rest, and says what
 * that thing is for. The tour drives the navigation itself, so the person watching
 * only ever presses Next.
 *
 * TARGETED BY WHAT THINGS SAY, not by ids. A data-tour attribute sprinkled through
 * twelve screens is twelve places to forget, and a step pointing at a stale id lights
 * a ring over the wrong thing, which is worse than lighting nothing. The helpers
 * below find a panel by its heading and a tab by its label, which is also how a
 * person reading the screen would find them.
 *
 * COPY RULES: names not counts, no invented numbers, no em dashes, and the score is
 * described as what it is - one holistic picture - never "a number".
 */

export interface TourStep {
  title: string;
  body: string;
  /** Where the step happens. The tour navigates and waits for the screen. */
  route?: string;
  /** Something to do before pointing: open a tab, click into a record. */
  prep?: () => Promise<void> | void;
  /** What to light up. Not found means a centred card and no spotlight. */
  target?: () => Element | null;
}

/* ---- finding things by what they say ---- */

const panels = (): HTMLElement[] => [...document.querySelectorAll<HTMLElement>('.page .panel')];

export const panelNamed = (heading: string): HTMLElement | null =>
  panels().find((p) => p.querySelector('h2, h3')?.textContent?.trim().startsWith(heading)) ?? null;

export const inPanel = (heading: string, sel: string): Element | null =>
  panelNamed(heading)?.querySelector(sel) ?? null;

export const buttonSaying = (label: string): HTMLElement | null =>
  [...document.querySelectorAll<HTMLElement>('.page button, .page a')]
    .find((b) => b.textContent?.trim() === label) ?? null;

export const tabSaying = (label: string): HTMLElement | null =>
  [...document.querySelectorAll<HTMLElement>('.page .tabstrip .tab')]
    .find((b) => b.textContent?.trim() === label) ?? null;

const wait = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Click a tab and give the screen a moment. A tab is a state change, not a route. */
export const openTab = async (label: string): Promise<void> => {
  tabSaying(label)?.click();
  await wait(300);
};

/* ONE PARAGRAPH, SAID ONCE PER SEAT IN THE SAME WORDS, because it is the same model
   on every seat. If this ever differs between two scripts, one of them is wrong. */
const SCORE = 'It is not an attendance number. It is one holistic picture of where this '
  + 'person stands. 100 is the ideal member: training, known by name, holding their rhythm. '
  + 'Nobody starts there. Each member sits somewhere below it and moves up or down as their '
  + 'attendance, their relationship anchors and their health signals build or slip. A low '
  + 'score is the one that needs a person. Red, 40 and under, is who to see this week. '
  + 'Amber, 41 to 75, is slipping and needs a warm word. Green, above 75, is steady: leave '
  + 'them be. Members on Care Track and members in their first 90 days are never scored.';

const SIGN_OFF = (others: string): TourStep => ({
  title: 'That is the seat',
  body: `Sign out and open ${others} to see their side of the same people. `
    + 'Replay this any time from Walkthrough in the top bar.',
});

export const TOURS: Record<string, TourStep[]> = {
  /* ---------------- Support Coach ---------------- */
  sales: [
    {
      title: 'This is the Support Coach desk',
      body: 'Acrostic is built for the people on the floor. Every screen answers one '
        + 'question: who needs a person today? This takes about two minutes.',
    },
    {
      route: '#/home-sales', target: () => document.querySelector('.sidebar nav'),
      title: 'Four screens',
      body: 'Nothing to configure. A support coach’s whole day fits in Dashboard, '
        + 'Classes, Leads and Members, and each one opens on the people who are theirs.',
    },
    {
      route: '#/home-sales', target: () => panelNamed('Going quiet'),
      title: 'Members, not numbers',
      body: 'The people in this book whose rhythm has slipped, worst first. Each line is '
        + 'that person’s own pattern in their own numbers, not a template: "was three '
        + 'a week, now hardly at all" is a sentence about them.',
    },
    {
      route: '#/home-sales', target: () => inPanel('Going quiet', '.score'),
      title: 'The score is a whole picture', body: SCORE,
    },
    {
      route: '#/home-sales', target: () => panelNamed("Nobody's anchor"),
      title: 'The people nobody here knows',
      body: 'A member known by nobody is the first one lost, however often they train. '
        + 'This is not a risk list and nothing on it is scored or chased: it is the gap '
        + 'the whole product is about, made into something you can close.',
    },
    {
      route: '#/home-sales', target: () => inPanel("Nobody's anchor", '.rowacts button'),
      title: 'And you can close it here',
      body: 'Recording who knows somebody takes a name and one true sentence about what '
        + 'makes it two-way. Saying "they are mine" without that sentence is an '
        + 'assignment, so the form asks for it even when you are the one who knows them.',
    },
    {
      route: '#/home-sales', target: () => document.querySelector('.lcstrip'),
      title: 'One journey, one set of words',
      body: 'Lead to member to at risk to win-back, with the change against the same board '
        + 'a week ago. The stages overlap, so the arrows are a reading order and not a '
        + 'funnel: a member can be active and at risk at the same moment. Every cell '
        + 'opens the people behind it.',
    },
    {
      route: '#/home-sales', target: () => panelNamed('Follow-up pipeline'),
      title: 'The loop is closed by an outcome',
      body: 'Why the person is here, what happened, what to do and by when, and what came '
        + 'back. Without the last column nothing can ever learn which actions bring '
        + 'people back, which is why the outcome is recorded here rather than remembered.',
    },
    {
      route: '#/leads', target: () => document.querySelector('.kanban'),
      title: 'Every lead has a name on it',
      body: 'The board opens on your own leads, with everyone one click away. A brand-new '
        + 'enquiry says "routed to" rather than naming an owner, because being on '
        + 'somebody’s list is not the same as having been spoken to.',
    },
    {
      route: '#/classes',
      target: () => document.querySelector('.ctx-bar') ?? document.querySelector('.page .panel'),
      title: 'Who to pay attention to today',
      body: 'The register is the only moment in the product when staff are physically in '
        + 'front of these people. First three classes, back after a gap, needs attention: '
        + 'three groups, each opening its names.',
    },
    SIGN_OFF('Coach, Manager or Owner'),
  ],

  /* ---------------- Coach ---------------- */
  coach: [
    {
      title: 'This is the Coach desk',
      body: 'A coach sees the people who train in their sessions, and nothing else. '
        + 'This takes about a minute.',
    },
    {
      route: '#/home-coach', target: () => panelNamed('Going quiet'),
      title: 'Your people, worst first',
      body: 'The members who train with you whose rhythm has slipped. The same model the '
        + 'support coach and the owner read: nobody keeps a private version of who is at risk.',
    },
    {
      route: '#/home-coach', target: () => inPanel('Going quiet', '.score'),
      title: 'The score is a whole picture', body: SCORE,
    },
    {
      route: '#/home-coach', target: () => panelNamed("Nobody's anchor"),
      title: 'Known to nobody',
      body: 'Anyone in your sessions with no anchor on record. You are in the room with '
        + 'these people, so the move is an introduction, not a message.',
    },
    {
      route: '#/classes', target: () => document.querySelector('.page table'),
      title: 'Today’s sessions',
      body: 'Your timetable, who is in, and the members in their first 90 days whose home '
        + 'session this is, by name. A name you say out loud in week one is the whole '
        + 'difference between a member and a visitor.',
    },
    {
      route: '#/classes',
      prep: async () => { buttonSaying('Attendance')?.click(); await wait(450); },
      target: () => document.querySelector('.register'),
      title: 'Attendance is one click per person',
      body: 'The list is already in the order a register is read and it does not reorder '
        + 'as you tick. Beside each name is what would change how you say hello: first '
        + 'class, back after a long gap, nobody has spoken to them in six weeks.',
    },
    {
      route: '#/coach', target: () => panelNamed('PT clients'),
      title: 'My PT clients',
      body: 'Your personal-training roster, and beside it everyone who trains in your '
        + 'sessions without being a client. Ordered by who needs a word. The two groups '
        + 'the model declines to score sit underneath, unranked and named.',
    },
    SIGN_OFF('Support Coach, Manager or Owner'),
  ],

  /* ---------------- Manager ---------------- */
  manager: [
    {
      title: 'This is the Manager day',
      body: 'A manager sees the whole floor’s people and the changes waiting on a '
        + 'human. This takes about a minute.',
    },
    {
      route: '#/home-manager', target: () => panelNamed('Approvals'),
      title: 'A person approves changes',
      body: 'Price changes, plan moves, cancellations, leads being written off. Each one '
        + 'waits for a person, with the coach who asked named on the row. Approving '
        + 'applies the change to the record it names.',
    },
    {
      route: '#/home-manager', target: () => inPanel('Going quiet', '.score'),
      title: 'The score is a whole picture', body: SCORE,
    },
    {
      route: '#/home-manager', target: () => panelNamed('Care Track and Hold'),
      title: 'Away is not gone',
      body: 'A member on Care Track or Hold is never scored, ranked or chased. What stops '
        + 'that becoming abandonment is the review date, so a review falling due is on '
        + 'this screen and a member with no review date at all leads the list.',
    },
    {
      route: '#/campaigns', target: () => document.querySelector('.page table'),
      title: 'Campaigns with honest numbers',
      body: 'Audiences come from live segments, so two screens never disagree about who '
        + 'would be reached. Sending is off: an active campaign says "not yet sent" '
        + 'rather than showing an open rate it cannot have.',
    },
    {
      route: '#/segments', target: () => document.querySelector('.segcard'),
      title: 'Segments are people',
      body: 'Going quiet, Watch, New members, Champions, Known to nobody, Win-back. Each '
        + 'updates live from the score and the anchors, and every one opens its names. '
        + 'Care Track and Hold is there too, as a review list that refuses to be an audience.',
    },
    {
      route: '#/trainers', target: () => document.querySelector('.page table'),
      title: 'Coaches and trainers',
      body: 'Who teaches here, how much they have taught, and which of them can sign in. '
        + 'The support roster is listed separately: routing an enquiry and teaching a '
        + 'class are two different jobs, and this product has conflated them before.',
    },
    SIGN_OFF('Support Coach, Coach or Owner'),
  ],

  /* ---------------- Owner ---------------- */
  gym_admin: [
    {
      title: 'This is the Owner view',
      body: 'The owner sees the business, and the same people the floor sees. '
        + 'This takes about a minute.',
    },
    {
      route: '#/dashboard', target: () => document.querySelector('.panel.stats'),
      title: 'The one seat where a number may lead',
      body: 'Everywhere else in Acrostic a count is a thing that hides the people behind '
        + 'it. Here they are allowed to lead, on one condition: every one of them opens '
        + 'its names, and each of these does.',
    },
    {
      route: '#/dashboard', target: () => inPanel('Going quiet', '.score'),
      title: 'The score is a whole picture', body: SCORE,
    },
    {
      route: '#/dashboard', target: () => panelNamed('Anchor coverage'),
      title: 'The number this company is about',
      body: 'How many of your members are known by name to somebody here. There is no '
        + 'survey score in this product because there is no survey: this is the real '
        + 'measure, and the members outside it are the first ones you lose.',
    },
    {
      route: '#/dashboard', target: () => panelNamed('How the floor is doing'),
      title: 'One model, every seat',
      body: 'The same tiers the support coach and the coach work from. Nobody keeps a '
        + 'private version of who is at risk, and the two groups the model declines to '
        + 'score are named under the bar rather than folded into Green.',
    },
    {
      route: '#/settings', target: () => panelNamed('Automations'),
      title: 'Automations are people’s moves',
      body: 'First response within the hour, going quiet, the first 90 days, '
        + 'win-back. Each is a named person doing something at the right time rather than '
        + 'a robot, and none of them leads with a discount.',
    },
    {
      route: '#/accounts', target: () => document.querySelector('.page table'),
      title: 'The gym runs its own logins',
      body: 'You hand out every seat except ours; a manager can hand out two. Nobody can '
        + 'lock themselves out and the gym always keeps an Owner. Those are refusals in '
        + 'the product, not warnings in a manual.',
    },
    SIGN_OFF('Support Coach, Coach or Manager'),
  ],
};
